import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { Pagination } from "@mui/material";
import { FiSearch, FiUserPlus } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { server } from "../../server";
import { toast } from "react-toastify";

export default function EmployeesList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState("");
  const [employeesList, setEmployeesList] = useState([]);
  const [employeesCount, setEmployeesCount] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getAllEmployees();
  }, [pageNo, pageSize, searchParams]);

  const getAllEmployees = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/auth/getAllUsers`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          pageNo,
          pageSize,
          searchParams,
        },
      });

      setEmployeesList(data?.data);
      setEmployeesCount(data?.count);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error(e?.response?.data);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-6xl mx-auto p-6 bg-white rounded-xl shadow-lg"
    >
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl font-extrabold text-slate-800">
          Employees List
        </h1>
        <button
          onClick={() => navigate("../registerEmployee")}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-full font-semibold shadow-md transition"
          aria-label="Add Employee"
        >
          <FiUserPlus size={20} />
          Add Employee
        </button>
      </div>

      <div className="mb-6 max-w-md mx-auto relative">
        <FiSearch
          className="absolute top-3 left-3 text-slate-400 pointer-events-none"
          size={20}
        />
        <input
          type="search"
          placeholder="Search by name, email or position..."
          value={searchParams}
          onChange={(e) => {
            setSearchParams(e.target.value);
            setPageNo(1);
          }}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
        <table className="w-full table-auto text-left">
          <thead className="bg-blue-50">
            <tr>
              {["Name", "Email Id", "Mobile No.", "Position", "User Type"].map(
                (header) => (
                  <th
                    key={header}
                    className="px-6 py-3 uppercase text-sm font-semibold text-blue-700 tracking-wide"
                  >
                    {header}
                  </th>
                )
              )}
            </tr>
          </thead>
          <tbody>
            {!loading && employeesList?.length === 0 ? (
              <tr>
                <td
                  colSpan={3}
                  className="text-center p-6 text-slate-500 font-medium"
                >
                  No employees found.
                </td>
              </tr>
            ) : (
              employeesList?.map((e, i) => (
                <tr
                  key={i}
                  className={`transition cursor-pointer ${
                    i % 2 === 0 ? "bg-white" : "bg-blue-50"
                  } hover:bg-blue-100`}
                >
                  <td className="px-6 py-4 text-slate-800 font-medium">
                    {e?.fullName || "---"}
                  </td>
                  <td
                    className="px-6 py-4 text-slate-600 cursor-default"
                    onClick={(event) => event?.stopPropagation()}
                  >
                    {e?.email ? (
                      <a
                        href={`mailto:${e?.email}`}
                        target="_blank"
                        className="text-blue-700"
                      >
                        {e?.email}
                      </a>
                    ) : (
                      "---"
                    )}
                  </td>
                  <td className="px-6 py-4 text-slate-600">
                    {e?.mobileNo || "---"}
                  </td>
                  <td className="px-6 py-4 text-slate-700">
                    {e?.position || "---"}
                  </td>
                  <td className="px-6 py-4 text-slate-700 capitalize font-semibold">
                    {e?.userType || "---"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-8 flex justify-center">
        <Pagination
          count={employeesCount}
          page={pageNo}
          onChange={(_, value) => setPageSize(value)}
          color="primary"
          shape="rounded"
          showFirstButton
          showLastButton
        />
      </div>
    </motion.div>
  );
}
